import { ReportExportButtons } from '../components/ReportExportButtons'
import { useCallback, useEffect, useState } from 'react'
import {
  productivityApi,
  type ProductivityDefinitions,
  type ProductivityKey,
  type ProductivityReport,
  type SalesAgentReport,
} from '../api/productivity'
import { Card, CardHeader, Callout, Tabs, DataTable, DataTableEmptyRow, Page, PageHeader, TextField, FilterBar, StatusPill, CircularProgress } from '../components/ui'

/**
 * F-072 (REQ-270-001, REQ-099-001) — produttività per utente e report per agente. Le definizioni dei KPI sono
 * sempre visibili. Nessun importo, nessuna classifica e nessun punteggio: le righe sono in ordine di id. Il tempo
 * medio di risposta, le opportunità, il fatturato e l'incassato non esistono e il server lo dichiara nelle note.
 */

type Tab = 'productivity' | 'agents'

const COLUMNS: ProductivityKey[] = [
  'tasksCompleted',
  'tasksOverdueOpen',
  'leadsWorked',
  'callAttempts',
  'appointmentsCompleted',
  'appointmentsNoShow',
  'practicesCreated',
  'practicesProcessed',
  'reworkErrors',
  'outOfSlaNow',
  'avgResponseMinutes',
]

const formatNum = (v: number | null): string => (v === null ? '—' : v.toLocaleString('it-IT'))

/** Frazione dal server (0.25 = +25%); null con base 0 e mai "0%". */
function formatDelta(delta: number, pct: number | null): string {
  const sign = delta > 0 ? '+' : ''
  const abs = `${sign}${formatNum(delta)}`
  if (pct === null) return `${abs} (nuovo)`
  return `${abs} (${sign}${(pct * 100).toLocaleString('it-IT', { maximumFractionDigits: 1 })}%)`
}

const COMPARED: [string, string][] = [
  ['totalLeads', 'Lead'],
  ['contacted', 'Contattati'],
  ['appointmentsCompleted', 'Appuntamenti fatti'],
  ['noShow', 'No show'],
  ['contracts', 'Contratti'],
  ['activations', 'Attivazioni (funnel)'],
  ['practicesCreated', 'Pratiche inserite'],
  ['genuineActivations', 'Attivazioni dell’agente'],
]

const eur = (v: number): string => v.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })

export function ProductivityReportPage() {
  const [tab, setTab] = useState<Tab>('productivity')
  const [form, setForm] = useState<{ from: string; to: string; userId: string; teamOf: string }>({ from: '', to: '', userId: '', teamOf: '' })
  const [applied, setApplied] = useState(form)
  const [defs, setDefs] = useState<ProductivityDefinitions | null>(null)
  const [report, setReport] = useState<ProductivityReport | null>(null)
  const [agents, setAgents] = useState<SalesAgentReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    productivityApi
      .definitions()
      .then(setDefs)
      .catch(e => setError(e instanceof Error ? e.message : 'Errore'))
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      if (tab === 'productivity') setReport(await productivityApi.report(applied))
      else setAgents(await productivityApi.salesAgents(applied))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [tab, applied])

  useEffect(() => {
    void load()
  }, [load])

  const defOf = (key: ProductivityKey) => defs?.kpis.find(k => k.key === key)

  return (
    <Page>
      <PageHeader title="Produttività" icon="trending_up" actions={<ReportExportButtons title="Produttività" />} />

      <FilterBar
        onSubmit={() => setApplied({ ...form })}
        onReset={() => {
          const empty = { from: '', to: '', userId: '', teamOf: '' }
          setForm(empty)
          setApplied(empty)
        }}
      >
        {(
          [
            ['from', 'Da', 'date'],
            ['to', 'A', 'date'],
            ['userId', 'Utente (id)', 'number'],
            ['teamOf', 'Team di (id utente)', 'number'],
          ] as const
        ).map(([key, label, type]) => (
          <TextField
            key={key}
            label={label}
            type={type}
            value={form[key]}
            onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
          />
        ))}
      </FilterBar>
      <div className="text-muted md-typescale-body-small">
        Senza date: ultimi 30 giorni. Vedi solo te stesso; un responsabile vede anche il proprio team.
      </div>

      {error && <Callout tone="error">Non è stato possibile caricare il report. {error}</Callout>}

      <Tabs
        aria-label="Sezioni del report"
        value={tab}
        onChange={id => setTab(id as Tab)}
        items={[
          { id: 'productivity', label: 'Produttività' },
          { id: 'agents', label: 'Report per agente' },
        ]}
      />

      {loading && <div className="text-center"><div className="d-inline-block"><CircularProgress size="md" label="Caricamento..." /></div></div>}

      {tab === 'productivity' && report && (
        <>
          <div className="text-muted md-typescale-body-small mb-2">
            Dal {report.from} al {report.to} ({report.timeZone}). Le colonne «adesso» sono una fotografia e non dipendono dal periodo.
          </div>
          <div className="mb-3">
            <DataTable>
              <thead>
                <tr>
                  <th>Utente</th>
                  {COLUMNS.map(c => (
                    <th key={c} className="text-end md-num" title={defOf(c)?.formula}>
                      {defOf(c)?.label ?? c}
                      {defOf(c)?.provisional ? ' *' : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.rows.map(r => (
                  <tr key={r.userId}>
                    <td>{r.name ?? `Utente ${r.userId}`}</td>
                    {COLUMNS.map(c => (
                      <td key={c} className="text-end md-num">
                        {formatNum(r.metrics[c])}
                      </td>
                    ))}
                  </tr>
                ))}
                {report.rows.length === 0 && (
                  <DataTableEmptyRow colSpan={COLUMNS.length + 1} title="Nessun utente nel perimetro." />
                )}
              </tbody>
            </DataTable>
          </div>

          <Card>
            <CardHeader title="Come si calcola ogni misura" />
            <div className="p-4">
              {defs?.kpis.map(k => (
                <div key={k.key} className="mb-3 pb-3 border-bottom last:border-b-0">
                  <div className="md-typescale-emphasized-body-medium">
                    {k.label}
                    {k.provisional && <StatusPill tone="warning" className="ms-2">provvisorio</StatusPill>}
                    {k.snapshot && <StatusPill tone="neutral" className="ms-2">fotografia di adesso</StatusPill>}
                  </div>
                  <div className="md-typescale-body-small">{k.formula}</div>
                  <div className="text-muted md-typescale-body-small">Fonte: {k.source}</div>
                </div>
              ))}
            </div>
          </Card>
          <ul className="md-typescale-body-small text-muted">
            {[...(defs?.notes ?? []), ...report.notes].filter((n, i, a) => a.indexOf(n) === i).map(n => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </>
      )}

      {tab === 'agents' && agents && (
        <>
          <div className="text-muted md-typescale-body-small mb-2">
            Dal {agents.from} al {agents.to}, confrontato con il periodo precedente di pari durata ({agents.previousFrom} → {agents.previousTo}).
          </div>
          {agents.rows.length === 0 && <div className="text-muted">Nessun agente con attività nel perimetro.</div>}
          {agents.rows.map(r => (
            <Card key={r.agentId} className="mb-3">
              <CardHeader title={r.name ?? `Agente ${r.agentId}`} />
              <div>
                <DataTable variant="plain">
                  <thead>
                    <tr>
                      <th>Misura</th>
                      <th className="text-end md-num">Periodo</th>
                      <th className="text-end md-num">Precedente</th>
                      <th className="text-end md-num">Variazione</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COMPARED.map(([measure, label]) => {
                      const c = r.comparison.find(x => x.measure === measure)
                      if (!c) return null
                      return (
                        <tr key={measure}>
                          <td>{label}</td>
                          <td className="text-end md-num">{formatNum(c.current)}</td>
                          <td className="text-end md-num">{formatNum(c.previous)}</td>
                          <td className="text-end md-num">{formatDelta(c.delta, c.deltaPct)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </DataTable>
                {r.revenue && (
                  <DataTable variant="plain">
                    <thead>
                      <tr>
                        <th>Ricavo delle pratiche create</th>
                        <th className="text-end md-num">Periodo</th>
                        <th className="text-end md-num">Precedente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {([['expected', 'Presunto'], ['accrued', 'Maturato'], ['collected', 'Incassato']] as const).map(([k, label]) => (
                        <tr key={k}>
                          <td>{label}</td>
                          <td className="text-end md-num">{eur(r.revenue!.current[k])}</td>
                          <td className="text-end md-num">{eur(r.revenue!.previous[k])}</td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                )}
              </div>
            </Card>
          ))}
          <ul className="md-typescale-body-small text-muted">
            {agents.notes.map(n => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </>
      )}
    </Page>
  )
}
